import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import NotificationsBell from "@/components/layout/NotificationsBell";
import InstallPrompt from "@/components/layout/InstallPrompt";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Map, Fish, Package, Users, CreditCard,
  LogOut, Languages, Sprout, CalendarDays, Moon, Sun, ShieldCheck, Tractor, ShieldAlert, Wrench, Users2, Factory, BarChart3, Bot, Sparkles, Settings, KeyRound,
} from "lucide-react";

const NAV = [
  { to: "/", key: "dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/parcelles", key: "parcelles", icon: Map, testid: "nav-parcelles" },
  { to: "/timeline", key: "harvest_timeline", icon: CalendarDays, testid: "nav-timeline" },
  { to: "/travaux", key: "tasks", icon: Tractor, testid: "nav-tasks" },
  { to: "/sante", key: "health", icon: ShieldAlert, testid: "nav-health" },
  { to: "/pisciculture", key: "pisciculture", icon: Fish, testid: "nav-pisciculture" },
  { to: "/fournisseurs", key: "suppliers", icon: Package, testid: "nav-suppliers" },
  { to: "/equipements", key: "equipment_page", icon: Wrench, testid: "nav-equipment", roles: ["admin", "gérant"] },
  { to: "/equipes", key: "employees_page", icon: Users2, testid: "nav-employees", roles: ["admin", "gérant"] },
  { to: "/transformation", key: "transformation", icon: Factory, testid: "nav-transformation" },
  { to: "/analytics", key: "analytics_page", icon: BarChart3, testid: "nav-analytics" },
  { to: "/assistant", key: "ai_assistant", icon: Bot, testid: "nav-assistant" },
  { to: "/decouvrir", key: "discover", icon: Sparkles, testid: "nav-discover" },
  { to: "/clients", key: "clients", icon: Users, testid: "nav-clients" },
  { to: "/paiements", key: "payments", icon: CreditCard, testid: "nav-payments", roles: ["admin", "gérant", "comptable"] },
  { to: "/parametres", key: "settings", icon: Settings, testid: "nav-settings", roles: ["admin"] },
  { to: "/utilisateurs", key: "users", icon: ShieldCheck, testid: "nav-users", roles: ["admin"] },
  { to: "/licences", key: "licenses", icon: KeyRound, testid: "nav-licences", roles: ["admin"] },
];

export default function Layout({ children }) {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const { dark, toggleDark } = useTheme();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-background animated-texture">
      {/* Sidebar */}
      <aside className="hidden md:flex md:w-64 lg:w-72 flex-col border-r border-border bg-secondary texture-leaves-faint" data-testid="sidebar">
        <div className="px-6 py-6 border-b border-border">
          <div className="flex items-center gap-3">
            <img src="/logo-agrinova.png" alt="AgriNova" className="w-11 h-11 rounded-lg object-cover bg-white" data-testid="sidebar-logo" />
            <div>
              <div className="font-bold text-lg tracking-tight leading-tight">AgriNova</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{t("slogan")}</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.filter((n) => !n.roles || (user && n.roles.includes(user.role))).map(({ to, key, icon: Icon, testid }) => (
            <NavLink
              key={to} to={to} end={to === "/"}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground/80 hover:bg-primary/10 hover:text-primary"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {t(key)}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <button
              data-testid="lang-toggle"
              onClick={() => setLang(lang === "fr" ? "en" : "fr")}
              className="flex-1 flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2 text-sm hover:border-primary/50 transition"
            >
              <span className="flex items-center gap-2"><Languages className="w-4 h-4" /> {lang === "fr" ? "FR" : "EN"}</span>
            </button>
            <NotificationsBell />
            <button
              onClick={toggleDark}
              data-testid="theme-toggle"
              aria-label={dark ? t("light_mode") : t("dark_mode")}
              className="w-9 h-9 rounded-lg border border-border bg-card flex items-center justify-center hover:border-primary/50 transition-colors"
            >
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
          </div>
          {user && user.email && (
            <div className="text-xs text-muted-foreground">
              <div className="truncate font-medium text-foreground" data-testid="user-email">{user.email}</div>
              <div>{user.role === "admin" ? t("role_admin") : user.role}</div>
            </div>
          )}
          <Button
            variant="outline" size="sm" className="w-full justify-start gap-2"
            data-testid="logout-btn"
            onClick={async () => { await logout(); navigate("/login"); }}
          >
            <LogOut className="w-4 h-4" /> {t("logout")}
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-secondary border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <img src="/logo-agrinova.png" alt="AgriNova" className="w-9 h-9 rounded-lg object-cover bg-white" />
            <span className="font-bold">AgriNova</span>
          </div>
          <button
            onClick={() => setLang(lang === "fr" ? "en" : "fr")}
            className="text-xs font-semibold uppercase tracking-widest text-primary border border-primary/30 rounded-full px-3 py-1"
            data-testid="lang-toggle-mobile"
          >{lang === "fr" ? "EN" : "FR"}</button>
          <button
            onClick={toggleDark}
            data-testid="theme-toggle-mobile"
            className="w-7 h-7 rounded-full border border-border bg-card flex items-center justify-center"
          >{dark ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}</button>
        </div>
        <div className="overflow-x-auto flex gap-1 px-2 pb-2">
          {NAV.filter((n) => !n.roles || (user && n.roles.includes(user.role))).map(({ to, key, icon: Icon }) => (
            <NavLink key={to} to={to} end={to === "/"}
              className={({ isActive }) =>
                `flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium ${
                  isActive ? "bg-primary text-primary-foreground" : "bg-card border border-border"
                }`
              }>
              <Icon className="w-3.5 h-3.5" /> {t(key)}
            </NavLink>
          ))}
        </div>
      </div>

      <main className="flex-1 md:ml-0 pt-28 md:pt-0 p-4 md:p-8 max-w-full overflow-x-hidden grain-overlay">
        {children}
      </main>
      <InstallPrompt />
    </div>
  );
}
